import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export interface WhaleMovement {
  id: string;
  token_symbol: string;
  token_address: string | null;
  wallet_address: string;
  movement_type: 'accumulation' | 'distribution' | 'transfer';
  amount: number;
  amount_usd: number;
  block_number: number;
  transaction_hash: string;
  from_address: string;
  to_address: string;
  detected_at: string;
  created_at: string;
}

export interface WhaleSummary {
  id: string;
  token_symbol: string;
  token_address: string | null;
  accumulation_count: number;
  distribution_count: number;
  net_flow: number;
  net_flow_usd: number;
  whale_sentiment: 'strong_accumulation' | 'accumulation' | 'neutral' | 'distribution' | 'strong_distribution';
  top_whale_addresses: string[];
  analysis_period_hours: number;
  analyzed_at: string;
  created_at: string;
}

export const useWhaleSummaries = () => {
  return useQuery({
    queryKey: ['whale-summaries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('whale_summary')
        .select('*')
        .order('analyzed_at', { ascending: false });

      if (error) throw error;
      return data as WhaleSummary[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
  });
};

export const useWhaleSummary = (tokenSymbol: string) => {
  return useQuery({
    queryKey: ['whale-summary', tokenSymbol],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('whale_summary')
        .select('*')
        .eq('token_symbol', tokenSymbol.toUpperCase())
        .order('analyzed_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as WhaleSummary | null;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!tokenSymbol,
  });
};

export const useRecentWhaleMovements = (tokenSymbol?: string, limit = 20) => {
  return useQuery({
    queryKey: ['whale-movements', tokenSymbol, limit],
    queryFn: async () => {
      let query = supabase
        .from('whale_movements')
        .select('*')
        .order('detected_at', { ascending: false })
        .limit(limit);

      if (tokenSymbol) {
        query = query.eq('token_symbol', tokenSymbol.toUpperCase());
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as WhaleMovement[];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });
};

export const useTrackWhaleMovements = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('track-whale-movements');
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: 'Whale Tracking Complete',
        description: `Tracked ${data.movements_tracked} whale movements across ${data.tokens_analyzed} tokens`,
      });
      queryClient.invalidateQueries({ queryKey: ['whale-summaries'] });
      queryClient.invalidateQueries({ queryKey: ['whale-movements'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Tracking Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Helper to get sentiment color
export const getSentimentColor = (sentiment: WhaleSummary['whale_sentiment']): string => {
  switch (sentiment) {
    case 'strong_accumulation':
      return 'text-green-400';
    case 'accumulation':
      return 'text-green-300';
    case 'neutral':
      return 'text-muted-foreground';
    case 'distribution':
      return 'text-red-300';
    case 'strong_distribution':
      return 'text-red-400';
    default:
      return 'text-muted-foreground';
  }
};

// Helper to get sentiment label
export const getSentimentLabel = (sentiment: WhaleSummary['whale_sentiment']): string => {
  switch (sentiment) {
    case 'strong_accumulation':
      return 'Strong Buy Signal';
    case 'accumulation':
      return 'Accumulating';
    case 'neutral':
      return 'Neutral';
    case 'distribution':
      return 'Distributing';
    case 'strong_distribution':
      return 'Strong Sell Signal';
    default:
      return 'Unknown';
  }
};
